import { useState, useEffect, type JSX } from 'react';
import { Document, useMedplum } from '@medplum/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Stack, Text, Title, Loader, Box, Paper } from '@mantine/core';
import { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { DocumentService } from '../../services/DocumentService';

/**
 * Component for displaying uploaded documents
 */
export function ViewDocumentPage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('Document');
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isHtml, setIsHtml] = useState(false);
  
  // Get form ID from URL query parameter
  const params = new URLSearchParams(location.search);
  const formId = params.get('id');
  
  useEffect(() => {
    async function loadDocument() {
      setLoading(true);
      setError(null);
      
      try {
        if (!formId) {
          setLoading(false);
          setError('No document ID provided');
          return;
        }
        
        // Load the QuestionnaireResponse
        const { response, questionnaire } = await DocumentService.getFormById(medplum, formId);
        setQuestionnaireResponse(response);
        
        // Mark as viewed on load
        if (response.status !== 'completed') {
          const updatedResponse = {
            ...response,
            status: 'completed' as const
          };
          
          await medplum.updateResource(updatedResponse);
        }
        
        // Handle Questionnaire - extract content from rendering-xhtml extension
        if (questionnaire && questionnaire.resourceType === 'Questionnaire') {
          setTitle(questionnaire.title || 'Document');
          
          // Look for content in a 'content' linkId or in a 'document' linkId
          const contentItem = questionnaire.item?.find(
            item => item.linkId === 'content' || item.linkId === 'document'
          );
          
          if (contentItem) {
            // Look for rendering-xhtml extension
            const htmlExt = contentItem.extension?.find(
              ext => ext.url === 'http://hl7.org/fhir/StructureDefinition/rendering-xhtml'
            );
            
            if (htmlExt && htmlExt.valueString) {
              // HTML content
              setContent(htmlExt.valueString);
              setIsHtml(true);
            } else {
              // For attachments - check for a binary attachment URL in the Questionnaire
              const attachmentUrlExt = contentItem.extension?.find(
                ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/attachment-url'
              );
              
              if (attachmentUrlExt && attachmentUrlExt.valueUrl) {
                setDocumentUrl(attachmentUrlExt.valueUrl);
              } else {
                setContent('No document content available');
                setError('Document has no content or URL');
              }
            }
          } else {
            // No content item found
            setContent('No document content available');
            setError('Document structure is invalid - no content item found');
          }
        } else {
          // Fallback for old-style documents
          setContent('No document content available');
          setError('Referenced resource is not a Questionnaire - please notify support to update this document');
        }
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Error loading document');
      } finally {
        setLoading(false);
      }
    }
    
    loadDocument();
    
    // Clean up object URLs on unmount
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [medplum, formId]);
  
  if (loading) {
    return (
      <Box p="xl" style={{ display: 'flex', justifyContent: 'center' }}>
        <Loader size="lg" />
      </Box>
    );
  }
  
  return (
    <Document width={800}>
      {error && (
        <Alert color="yellow" mb="md">
          {error}
        </Alert>
      )}
      
      <Stack gap="lg">
        <Title order={2}>{title}</Title>
        
        {documentUrl ? (
          <iframe 
            src={documentUrl} 
            style={{ width: '100%', height: '600px', border: 'none' }} 
            title={title}
          />
        ) : content ? (
          <Paper p="md" withBorder>
            {isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: content }} />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</pre>
            )}
          </Paper>
        ) : (
          <Text>No document content available</Text>
        )}
        
        <Button onClick={() => navigate('/documents')} style={{ alignSelf: 'flex-start' }}>
          Back to Documents
        </Button>
      </Stack>
    </Document>
  );
}